import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, X } from "lucide-react";
import { AccountVerificationBadge } from "./AccountVerificationBadge";
import { useI18n } from "../lib/i18n";

/** The native modal traps focus; closing restores the row or filter trigger. */
export function MobileHiringSheet({ title, onClose, children, identityVerified, verifiedProfessions }: {
  title: string; onClose: () => void; children: ReactNode;
  identityVerified?: boolean; verifiedProfessions?: readonly string[];
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { t } = useI18n();
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => { dialog?.close(); trigger?.focus(); };
  }, []);
  return createPortal(
    <dialog ref={dialogRef} className="mobile-hiring-sheet" aria-label={title}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <header><h2 className="flex items-center gap-1.5">{title}<AccountVerificationBadge identityVerified={identityVerified} verifiedProfessions={verifiedProfessions} /></h2><button type="button" onClick={onClose} aria-label={t("hiring.mobile.close")}><X aria-hidden="true" /></button></header>
      {children}
    </dialog>, document.body,
  );
}

type MobileHiringRowProps = {
  name: string;
  photo?: string | null;
  title: string;
  status: string;
  statusLabel: string;
  meta: string;
  identityVerified?: boolean;
  professions?: readonly string[];
  onOpen: () => void;
};

/** Applicants, direct offers, and saved talent share this compact identity row. */
export function MobileHiringRow({ name, photo, title, status, statusLabel, meta, identityVerified, professions = [], onOpen }: MobileHiringRowProps) {
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const { t } = useI18n();
  return (
    <button type="button" className="mobile-hiring-row" onClick={onOpen} aria-label={`${t("hiring.mobile.details")}: ${name}`}>
      {photo && photo !== failedPhoto ? <img src={photo} alt="" onError={() => setFailedPhoto(photo)} />
        : <span className="mobile-hiring-avatar" aria-hidden="true">{name.split(/\s+/).slice(0, 2).map((part) => part[0] || "").join("").toUpperCase()}</span>}
      <span className="mobile-hiring-person">
        <span className="mobile-hiring-name"><strong>{name}</strong><AccountVerificationBadge identityVerified={identityVerified} verifiedProfessions={professions} /></span>
        <span className="mobile-hiring-role">{title}</span>
        <span className="mobile-hiring-meta"><span className={`mobile-hiring-status ${status}`}>{statusLabel}</span><span>{meta}</span></span>
      </span>
      <ChevronRight aria-hidden="true" />
    </button>
  );
}
